import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { LoadingScreen } from '@workspace/ui/components/loading-spinner'
import { useEffect } from 'react'
import { TermsAcceptanceDialog } from '@/features/legal/components/terms-acceptance-dialog'
import { CURRENT_TERMS_VERSION } from '@/features/legal/constants'
import { useCurrentUserQuery } from '../hooks/api'
import { consumeAuthRedirect, useSession } from '../lib/auth'
import { AppLayout } from './app-layout'
import { Login } from './login'
import { LoginLayout } from './login-layout'

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { data: session, isPending: isSessionPending } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { data: userResponse } = useCurrentUserQuery({
    enabled: !!session,
  })

  const userData = userResponse?.data
  const onboardingCompleted = userData?.onboardingCompleted ?? false
  const termsAccepted = userData?.termsVersion === CURRENT_TERMS_VERSION

  useEffect(() => {
    if (!userData) return

    const isOnJoinRoute = location.pathname.startsWith('/join')
    const isOnSharedRoute = location.pathname.startsWith('/s/')
    const isOnRootRoute = location.pathname === '/'

    if (onboardingCompleted && isOnJoinRoute) {
      // Completed onboarding, redirect to stored URL or home
      const redirectUrl = consumeAuthRedirect()
      queryClient.clear()
      if (redirectUrl) {
        // Hard navigation: TanStack Router navigate doesn't reliably handle
        // arbitrary paths with query strings (e.g. /s/token?action=...).
        // queryClient.clear() above ensures fresh state at the target.
        window.location.href = redirectUrl
      } else {
        navigate({ to: '/', replace: true })
      }
    } else if (onboardingCompleted && isOnRootRoute) {
      // Already completed onboarding, check if there's a pending redirect
      const redirectUrl = consumeAuthRedirect()
      if (redirectUrl) {
        queryClient.clear()
        window.location.href = redirectUrl
      }
    } else if (!onboardingCompleted && !isOnJoinRoute && !isOnSharedRoute) {
      navigate({ to: '/join', replace: true })
    }
  }, [userData, onboardingCompleted, navigate, location.pathname, queryClient.clear])

  const isLoading = isSessionPending || (!!session && !userData)

  return (
    <>
      <LoadingScreen show={isLoading} />
      {session ? (
        onboardingCompleted ? (
          <AppLayout>
            {!isLoading && !termsAccepted && <TermsAcceptanceDialog />}
            {!isLoading && children}
          </AppLayout>
        ) : (
          !isLoading && children
        )
      ) : (
        <LoginLayout>
          <Login />
        </LoginLayout>
      )}
    </>
  )
}
